/**
 * useMemberFieldGroups — fetches all org field groups + a single member's
 * persisted instances and exposes a `saveGroup(groupId, instances)` callback.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  memberFieldGroupsAPI,
  MemberFieldGroup,
} from '@/services/api/memberFieldGroups';

interface UseMemberFieldGroupsReturn {
  groups: MemberFieldGroup[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  saveGroup: (groupId: number, instances: Record<string, any>[]) => Promise<MemberFieldGroup | null>;
}

function extractError(err: any, fallback: string): string {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((e: any) => e.msg || String(e)).join('; ');
  return detail || err?.message || fallback;
}

export default function useMemberFieldGroups(memberId: number | null | undefined): UseMemberFieldGroupsReturn {
  const [groups, setGroups] = useState<MemberFieldGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!memberId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await memberFieldGroupsAPI.list(memberId);
      setGroups(data.groups);
    } catch (err: any) {
      setError(extractError(err, 'Failed to load member field groups'));
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const saveGroup = useCallback(
    async (groupId: number, instances: Record<string, any>[]) => {
      if (!memberId) return null;
      try {
        const updated = await memberFieldGroupsAPI.setInstances(memberId, groupId, instances);
        setGroups((prev) => prev.map((g) => (g.field_group_id === groupId ? updated : g)));
        return updated;
      } catch (err: any) {
        setError(extractError(err, 'Failed to save group'));
        return null;
      }
    },
    [memberId]
  );

  return { groups, loading, error, refetch, saveGroup };
}
